/**
 * Inbound integration keys (`pck_int_*`) for external storefronts that
 * push orders, payments, and clients INTO PeptideClients. Distinct from
 * the operator-managed `pck_live_*` read keys in `apiKeys.ts`:
 *
 *   * `pck_live_*` (apiKeys.ts) — outbound: 3rd-party callers READ
 *     PeptideClients data via /v1/*.
 *   * `pck_int_*`  (this file)  — inbound: WooCommerce / Zapier / Make /
 *     custom shops PUSH orders + payments + clients INTO PeptideClients
 *     via /integrations/v1/*. Optional HMAC signature secret.
 *
 * Wraps the SECURITY DEFINER RPCs in `core.rpc_*_integration_key*` /
 * `core.rpc_list_integration_requests` / `core.rpc_integration_key_stats`.
 * Every mutation is org-admin-gated inside the RPC.
 *
 * The plaintext key + signing secret are returned ONCE at create time
 * and never again. UI shows them in a one-time reveal modal; we
 * intentionally don't persist them on the client.
 *
 * Cast pattern: `(supabase() as any).rpc(...)` because the generated
 * Database types don't yet know about these RPCs (codegen runs at deploy).
 * Mirrors `apiKeys.ts` and `webhooks.ts`.
 */
import { supabase } from '../client';

export type IntegrationKeyScope =
  | 'orders:write'
  | 'orders:cancel'
  | 'payments:write'
  | 'payments:refund'
  | 'clients:write';

export type IntegrationKeyPayloadFormat = 'peptideclients' | 'woocommerce';

export type IntegrationKeySourceLabel =
  | 'custom'
  | 'woocommerce'
  | 'zapier'
  | 'make'
  | 'shopify'
  | 'square'
  | 'bigcommerce'
  | 'stripe_checkout'
  | 'squarespace'
  | 'wix'
  | 'webflow'
  | 'other';

export interface IntegrationKeyRow {
  id: string;
  org_id: string;
  store_id: string | null;
  name: string;
  source_label: IntegrationKeySourceLabel;
  payload_format: IntegrationKeyPayloadFormat;
  key_prefix: string;
  signing_secret_prefix: string | null;
  require_signature: boolean;
  allowed_ips: string[];
  scopes: IntegrationKeyScope[];
  rate_limit_per_min: number;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

export interface IntegrationRequestRow {
  id: string;
  method: string;
  path: string;
  idempotency_key: string | null;
  external_id: string | null;
  status_code: number | null;
  error_code: string | null;
  error_detail: string | null;
  internal_id: string | null;
  request_received_at: string;
  request_completed_at: string | null;
  client_ip: string | null;
}

export interface IntegrationKeyStats {
  total_24h: number;
  errors_24h: number;
  last_seen: string | null;
}

// ---------- create ---------------------------------------------------------

export interface CreateIntegrationKeyInput {
  orgId: string;
  storeId: string;
  name: string;
  sourceLabel?: IntegrationKeySourceLabel;
  payloadFormat?: IntegrationKeyPayloadFormat;
  scopes?: IntegrationKeyScope[];
  allowedIps?: string[];
  rateLimitPerMin?: number;
  requireSignature?: boolean | null;
  expiresInDays?: number | null;
}

export interface CreateIntegrationKeyResult {
  id: string;
  keyPlain: string;
  signingSecretPlain: string | null;
  keyPrefix: string;
  signingSecretPrefix: string | null;
}

/**
 * Mint a new inbound integration key + signing secret. Plaintext is
 * shown ONCE — callers must surface it in a reveal modal and never
 * persist it on the client.
 */
export async function createIntegrationKey(
  input: CreateIntegrationKeyInput,
): Promise<CreateIntegrationKeyResult> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_create_integration_key', {
      p_org_id: input.orgId,
      p_store_id: input.storeId,
      p_name: input.name,
      p_source_label: input.sourceLabel ?? 'custom',
      p_payload_format: input.payloadFormat ?? 'peptideclients',
      p_scopes: input.scopes ?? ['orders:write'],
      p_allowed_ips: input.allowedIps ?? [],
      p_rate_limit_per_min: input.rateLimitPerMin ?? 120,
      p_require_signature: input.requireSignature ?? null,
      p_expires_in_days: input.expiresInDays ?? null,
    });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as
    | {
        id: string;
        key_plain: string;
        signing_secret_plain: string | null;
        key_prefix: string;
        signing_secret_prefix: string | null;
      }
    | undefined;
  if (!row) throw new Error('createIntegrationKey: no row returned');
  return {
    id: row.id,
    keyPlain: row.key_plain,
    signingSecretPlain: row.signing_secret_plain,
    keyPrefix: row.key_prefix,
    signingSecretPrefix: row.signing_secret_prefix,
  };
}

// ---------- list -----------------------------------------------------------

export interface ListIntegrationKeysInput {
  orgId: string;
  limit?: number;
  cursorAt?: string | null;
  cursorId?: string | null;
}

export async function listIntegrationKeys(
  input: ListIntegrationKeysInput,
): Promise<{
  rows: IntegrationKeyRow[];
  nextCursor: { at: string; id: string } | null;
}> {
  const limit = Math.max(1, Math.min(input.limit ?? 25, 100));
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_list_integration_keys', {
      p_org_id: input.orgId,
      p_cursor_at: input.cursorAt ?? null,
      p_cursor_id: input.cursorId ?? null,
      p_limit: limit,
    });
  if (error) throw error;
  const rows = ((data ?? []) as unknown) as IntegrationKeyRow[];
  const last = rows[rows.length - 1];
  return {
    rows,
    nextCursor:
      rows.length >= limit && last ? { at: last.created_at, id: last.id } : null,
  };
}

// ---------- revoke ---------------------------------------------------------

export async function revokeIntegrationKey(
  id: string,
  reason?: string | null,
): Promise<void> {
  const { error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_revoke_integration_key', {
      p_id: id,
      p_reason: reason ?? null,
    });
  if (error) throw error;
}

// ---------- request log ----------------------------------------------------

export interface ListIntegrationRequestsInput {
  keyId: string;
  limit?: number;
  cursorAt?: string | null;
  cursorId?: string | null;
}

export async function listIntegrationRequests(
  input: ListIntegrationRequestsInput,
): Promise<{
  rows: IntegrationRequestRow[];
  nextCursor: { at: string; id: string } | null;
}> {
  const limit = Math.max(1, Math.min(input.limit ?? 50, 200));
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_list_integration_requests', {
      p_key_id: input.keyId,
      p_cursor_at: input.cursorAt ?? null,
      p_cursor_id: input.cursorId ?? null,
      p_limit: limit,
    });
  if (error) throw error;
  const rows = ((data ?? []) as unknown) as IntegrationRequestRow[];
  const last = rows[rows.length - 1];
  return {
    rows,
    nextCursor:
      rows.length >= limit && last
        ? { at: last.request_received_at, id: last.id }
        : null,
  };
}

// ---------- stats ----------------------------------------------------------

export async function getIntegrationKeyStats(
  keyId: string,
): Promise<IntegrationKeyStats> {
  const { data, error } = await (supabase() as any)
    .schema('core')
    .rpc('rpc_integration_key_stats', { p_key_id: keyId });
  if (error) throw error;
  const row = (Array.isArray(data) ? data[0] : data) as
    | { total_24h: number | string; errors_24h: number | string; last_seen: string | null }
    | undefined;
  if (!row) {
    return { total_24h: 0, errors_24h: 0, last_seen: null };
  }
  return {
    total_24h: typeof row.total_24h === 'string' ? Number(row.total_24h) : row.total_24h,
    errors_24h:
      typeof row.errors_24h === 'string' ? Number(row.errors_24h) : row.errors_24h,
    last_seen: row.last_seen,
  };
}
